import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  useTheme,
  Tooltip,
} from '@mui/material';
import TvOffIcon from '@mui/icons-material/TvOff';
import { toggleBlackScreenError } from '../../api/client';

function BlackScreenCard({ pipeline, isActive, onToggle }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);

  const hasS3Bucket = !!pipeline?.s3_bucket_id;

  const handleClick = async () => {
    if (!hasS3Bucket) {
      return;
    }
    try {
      setLoading(true);
      await toggleBlackScreenError(pipeline.id);
      if (onToggle) {
        await onToggle();
      }
    } catch (error) {
      console.error('Failed to toggle Black Screen error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTooltipTitle = () => {
    if (!hasS3Bucket) {
      return 'No S3 bucket configured. Edit the pipeline to set an S3 bucket for black screen content.';
    }
    return isActive
      ? 'Click to restore original manifest URLs'
      : 'Click to swap manifests to black screen VOD content';
  };

  return (
    <Tooltip title={getTooltipTitle()}>
      <Card
        sx={{
          background: isActive
            ? isDark
              ? 'rgba(33, 33, 33, 0.8)'
              : 'rgba(33, 33, 33, 0.1)'
            : hasS3Bucket
            ? isDark
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)'
            : isDark
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          border: isActive
            ? `2px solid ${isDark ? '#424242' : '#616161'}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          opacity: hasS3Bucket ? 1 : 0.5,
          transition: 'all 0.3s ease',
          '&:hover': hasS3Bucket
            ? {
                transform: 'translateY(-2px)',
                boxShadow: isActive
                  ? `0 4px 12px ${isDark ? 'rgba(66,66,66,0.3)' : 'rgba(66,66,66,0.3)'}`
                  : `0 4px 12px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
              }
            : {},
        }}
      >
        <CardActionArea
          onClick={handleClick}
          disabled={loading || !hasS3Bucket}
          sx={{
            cursor: hasS3Bucket ? 'pointer' : 'not-allowed',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 1.5,
              px: 1,
              minHeight: 80,
              justifyContent: 'center',
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#424242' }} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <TvOffIcon
                  sx={{
                    fontSize: 28,
                    color: isActive
                      ? isDark
                        ? '#bdbdbd'
                        : '#424242'
                      : hasS3Bucket
                      ? isDark
                        ? 'rgba(255,255,255,0.3)'
                        : 'rgba(0,0,0,0.26)'
                      : isDark
                      ? 'rgba(255,255,255,0.2)'
                      : 'rgba(0,0,0,0.15)',
                    animation: isActive ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isActive
                      ? isDark
                        ? '#bdbdbd'
                        : '#424242'
                      : hasS3Bucket
                      ? 'text.secondary'
                      : 'text.disabled',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  Black Screen
                </Typography>
                {isActive && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      color: isDark ? '#9e9e9e' : '#757575',
                    }}
                  >
                    ACTIVE
                  </Typography>
                )}
                {!hasS3Bucket && !isActive && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      color: 'text.disabled',
                    }}
                  >
                    No S3 bucket
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  );
}

export default BlackScreenCard;
